import { InfoCircleOutlined } from '@ant-design/icons'
import { Card, Input, Radio, Tag, Tooltip } from 'antd'
import {
  ConsequenceOfFailure,
  getCofModifier,
  TriState,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { AlertTriangle } from 'lucide-react'
import { FC } from 'react'
import styled from 'styled-components'

const { TextArea } = Input

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  border: 2px solid ${({ theme }) => theme.forecast.border};
  background: ${({ theme }) => theme.forecast.background1};

  .ant-card-head {
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const InfoIcon = styled(InfoCircleOutlined)`
  font-size: 14px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  cursor: help;

  &:hover {
    color: ${({ theme }) => theme.forecast.primary};
  }
`

const RadioLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
`

const ModifierDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const ModifierLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-weight: 500;
`

const ModifierValue = styled.span<{ $value: number }>`
  font-size: 18px;
  font-weight: 700;
  color: ${({ $value }) =>
    $value >= 1.0 ? '#ff4d4f' : $value >= 0.8 ? '#faad14' : '#52c41a'};
`

const JustificationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const JustificationLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`

export interface ConsequenceOfFailureSectionProps {
  cof: ConsequenceOfFailure
  onChange: (cof: ConsequenceOfFailure) => void
  readonly?: boolean
}

export const ConsequenceOfFailureSection: FC<
  ConsequenceOfFailureSectionProps
> = ({ cof, onChange, readonly }) => {
  const modifier = getCofModifier(cof.level)

  const handleLevelChange = (level: TriState) => {
    onChange({ ...cof, level })
  }

  const handleJustificationChange = (justification: string) => {
    onChange({ ...cof, justification })
  }

  const modifierImpact =
    modifier >= 1.0
      ? 'Increases required coverage'
      : modifier < 1.0
        ? 'Decreases required coverage'
        : 'Neutral impact'

  return (
    <SectionCard
      title={
        <TitleRow>
          <AlertTriangle size={18} />
          Consequence of Failure (CoF) Modifier
          <Tooltip
            title='The Consequence of Failure modifier applies globally to all zones and adjusts the required inspection coverage based on the potential impact of equipment failure. Higher consequence equipment requires more extensive coverage to ensure integrity.'
            placement='right'
            overlayStyle={{ maxWidth: 400 }}>
            <InfoIcon />
          </Tooltip>
        </TitleRow>
      }>
      <SectionContent>
        <RadioGroup>
          <RadioLabel>Consequence Level</RadioLabel>
          <Radio.Group
            value={cof.level}
            disabled={readonly}
            onChange={(e) => handleLevelChange(e.target.value)}>
            <Radio value='low'>
              Low{' '}
              <Tag color='green' style={{ marginLeft: 8 }}>
                0.6×
              </Tag>
            </Radio>
            <Radio value='medium'>
              Medium{' '}
              <Tag color='gold' style={{ marginLeft: 8 }}>
                0.8×
              </Tag>
            </Radio>
            <Radio value='high'>
              High{' '}
              <Tag color='red' style={{ marginLeft: 8 }}>
                1.0×
              </Tag>
            </Radio>
          </Radio.Group>
        </RadioGroup>

        <ModifierDisplay>
          <ModifierLabel>Current Modifier:</ModifierLabel>
          <ModifierValue $value={modifier}>{modifier}×</ModifierValue>
          <Tag
            color={
              modifier >= 1.0 ? 'red' : modifier >= 0.8 ? 'gold' : 'green'
            }>
            {modifier >= 1.0 ? '↑' : modifier < 1.0 ? '↓' : '→'}
          </Tag>
          <span style={{ fontSize: 13, color: '#666', marginLeft: 'auto' }}>
            {modifierImpact}
          </span>
        </ModifierDisplay>

        <JustificationSection>
          <JustificationLabel>
            Justification / Reasoning
            <Tooltip
              title='Consequence of failure should consider the potential safety, environmental, and economic impacts of equipment failure. Higher consequence equipment typically requires more rigorous inspection strategies to maintain integrity assurance. (HOIS-RP-103)'
              placement='right'
              overlayStyle={{ maxWidth: 400 }}>
              <InfoIcon />
            </Tooltip>
          </JustificationLabel>
          <TextArea
            value={cof.justification}
            onChange={(e) => handleJustificationChange(e.target.value)}
            disabled={readonly}
            placeholder='Explain the reasoning for this consequence of failure assessment...'
            rows={3}
            maxLength={1000}
            showCount
          />
        </JustificationSection>
      </SectionContent>
    </SectionCard>
  )
}
